import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  User,
  Palette,
  Globe,
  Info,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Users,
  Crown,
  ChevronRight,
  Download,
  Target,
  Share2,
  Mail,
  ShieldCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useSession, useSignOut } from '@/hooks/use-auth'
import { useThemeStore, applyTheme } from '@/stores/theme'

type Theme = 'light' | 'dark' | 'system'

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

type ExportFormat = 'csv' | 'json' | 'vcard'

const exportFormats: { value: ExportFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'vcard', label: 'vCard' },
]

export default function SettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const signOut = useSignOut()
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv')
  const [exporting, setExporting] = useState(false)

  function handleThemeChange(newTheme: Theme) {
    setTheme(newTheme)
    applyTheme(newTheme)
  }

  async function handleExport() {
    setExporting(true)
    try {
      const res = await fetch('/api/export', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: exportFormat }),
      })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const filenameMatch = disposition.match(/filename="(.+)"/)
      const filename =
        filenameMatch?.[1] ?? `aime-export.${exportFormat === 'vcard' ? 'vcf' : 'zip'}`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  function handleSignOut() {
    signOut.mutate(undefined, {
      onSuccess: () => navigate('/sign-in', { replace: true }),
    })
  }

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <h1 className="font-heading mb-6 text-2xl font-bold">{t('settings.title')}</h1>

      <div className="space-y-4">
        {/* Profile */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-container-highest flex size-12 items-center justify-center rounded-full">
              <User className="text-primary size-6" />
            </div>
            <div>
              <p className="font-semibold">{session?.user.name}</p>
              <p className="text-on-surface-variant text-sm">{session?.user.email}</p>
            </div>
          </div>
        </section>

        {/* Appearance / Theme */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <div className="mb-3 flex items-center gap-2">
            <Palette className="text-on-surface-variant size-4" />
            <h2 className="font-semibold">{t('settings.appearance')}</h2>
          </div>
          <div className="flex gap-2">
            {themeOptions.map((opt) => {
              const Icon = opt.icon
              return (
                <button
                  key={opt.value}
                  onClick={() => handleThemeChange(opt.value)}
                  className={cn(
                    'flex flex-1 flex-col items-center gap-1 rounded-xl border p-3 text-sm transition-colors',
                    theme === opt.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-outline-variant/30 text-on-surface-variant hover:text-foreground hover:bg-surface-container-high',
                  )}
                >
                  <Icon className="size-5" />
                  <span>{opt.label}</span>
                </button>
              )
            })}
          </div>
        </section>

        {/* Language */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <div className="mb-3 flex items-center gap-2">
            <Globe className="text-on-surface-variant size-4" />
            <h2 className="font-semibold">{t('settings.language')}</h2>
          </div>
          <LanguageSwitcher />
        </section>

        {/* AI assistant connections */}
        <button
          type="button"
          onClick={() => navigate('/mcp/connect')}
          className="bg-surface-container-low hover:bg-surface-container-high w-full rounded-xl p-4 text-left transition-colors"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">AI assistants (MCP)</h2>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">
            Connect Claude or Codex to your AIME account
          </p>
        </button>
        {/* Connected Accounts */}
        <section
          className="bg-surface-container-low hover:bg-surface-container-high cursor-pointer rounded-xl p-4 transition-colors"
          onClick={() => navigate('/settings/connected-accounts')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="text-on-surface-variant size-4" />
              <h2 className="font-semibold">Connected Accounts</h2>
            </div>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">
            Connect Gmail or Outlook to auto-track email interactions
          </p>
        </section>

        {/* Targets */}
        <section
          className="bg-surface-container-low hover:bg-surface-container-high cursor-pointer rounded-xl p-4 transition-colors"
          onClick={() => navigate('/targets')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="text-on-surface-variant size-4" />
              <h2 className="font-semibold">Targets</h2>
            </div>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">
            Track relationship goals and introduction paths
          </p>
        </section>

        {/* Network graph */}
        <section
          className="bg-surface-container-low hover:bg-surface-container-high cursor-pointer rounded-xl p-4 transition-colors"
          onClick={() => navigate('/network')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Share2 className="text-on-surface-variant size-4" />
              <h2 className="font-semibold">Network</h2>
            </div>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">
            Visualize your relationship network graph
          </p>
        </section>

        {/* Subscription */}
        <section
          className="bg-primary/5 border-primary/30 hover:bg-primary/10 cursor-pointer rounded-xl border p-4 transition-colors"
          onClick={() => navigate('/settings/billing')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="text-primary size-4" />
              <h2 className="font-semibold">{t('billing.settingsLink')}</h2>
              {session?.user.role === 'premium' && (
                <span className="bg-primary/20 text-primary rounded-full px-2 py-0.5 text-xs font-medium">
                  Premium
                </span>
              )}
            </div>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">{t('billing.settingsDesc')}</p>
        </section>

        {/* Admin (admins only) */}
        {session?.user.isAdmin && (
          <section
            className="bg-surface-container-low hover:bg-surface-container-high cursor-pointer rounded-xl p-4 transition-colors"
            onClick={() => navigate('/admin')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-on-surface-variant size-4" />
                <h2 className="font-semibold">Admin</h2>
              </div>
              <ChevronRight className="text-outline size-4" />
            </div>
            <p className="text-on-surface-variant mt-1 text-sm">
              Manage users, premium grants, and billing
            </p>
          </section>
        )}

        {/* Find Duplicates */}
        <section
          className="bg-surface-container-low hover:bg-surface-container-high cursor-pointer rounded-xl p-4 transition-colors"
          onClick={() => navigate('/settings/duplicates')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="text-on-surface-variant size-4" />
              <h2 className="font-semibold">{t('duplicates.title')}</h2>
            </div>
            <ChevronRight className="text-outline size-4" />
          </div>
          <p className="text-on-surface-variant mt-1 text-sm">{t('duplicates.subtitle')}</p>
        </section>

        {/* Export Data */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <div className="mb-3 flex items-center gap-2">
            <Download className="text-on-surface-variant size-4" />
            <h2 className="font-semibold">{t('settings.exportData')}</h2>
          </div>
          <p className="text-on-surface-variant mb-3 text-sm">{t('settings.exportDesc')}</p>
          <div className="mb-3 flex gap-2">
            {exportFormats.map((fmt) => (
              <button
                key={fmt.value}
                onClick={() => setExportFormat(fmt.value)}
                className={cn(
                  'flex flex-1 items-center justify-center rounded-xl border p-2 text-sm transition-colors',
                  exportFormat === fmt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant/30 text-on-surface-variant hover:text-foreground hover:bg-surface-container-high',
                )}
              >
                {fmt.label}
              </button>
            ))}
          </div>
          <Button className="w-full rounded-xl" onClick={handleExport} disabled={exporting}>
            <Download className="mr-2 size-4" />
            {exporting ? t('settings.exporting') : t('settings.exportButton')}
          </Button>
        </section>

        {/* About */}
        <section className="bg-surface-container-low rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Info className="text-on-surface-variant size-4" />
            <h2 className="font-semibold">{t('settings.about')}</h2>
          </div>
          <p className="text-on-surface-variant mt-2 text-sm">Aime v1.0 — Your relationship CRM</p>
        </section>

        {/* Sign out */}
        <Button
          variant="outline"
          className="text-destructive border-outline-variant/30 hover:bg-destructive/10 w-full rounded-xl"
          onClick={handleSignOut}
          disabled={signOut.isPending}
        >
          <LogOut className="mr-2 size-4" />
          {signOut.isPending ? 'Signing out...' : t('settings.signOut')}
        </Button>
      </div>
    </div>
  )
}
